import { useMemo } from 'react'
import { Flex } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { BudgetsWidgetItem } from '@/components/BudgetsWidgetItem/BudgetsWidgetItem'
import { Loader } from '@/components/Loader/Loader'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import * as Common from '@/__generated__/queries/common'
import type { BudgetStats } from '@/__generated__/requests'
import { AdvisorsService, BudgetService } from '@/__generated__/requests'

import { formatMoney } from '@/helpers/formatMoney.helper'

import classes from './Budgets.module.css'

export function Budget({ userId }: { userId?: number }) {
  const isUser = !!userId

  const today = dayjs()

  const queryFn = isUser
    ? () =>
        AdvisorsService.advisorUsersControllerGetBudgets({
          endDate: today.endOf('month').toISOString(),
          startDate: today.startOf('month').toISOString(),
          userId: +userId,
        })
    : () =>
        BudgetService.budgetControllerGetAllBudgets({
          endDate: today.endOf('month').toISOString(),
          startDate: today.startOf('month').toISOString(),
        })

  const queryKey = isUser
    ? Common.UseAdvisorsServiceAdvisorUsersControllerGetBudgetsKeyFn({
        endDate: today.endOf('month').toISOString(),
        startDate: today.startOf('month').toISOString(),
        userId: +userId,
      })
    : Common.UseBudgetServiceBudgetControllerGetAllBudgetsKeyFn({
        endDate: today.endOf('month').toISOString(),
        startDate: today.startOf('month').toISOString(),
      })

  const { data, isFetching, isLoading } = useQuery({
    queryFn,
    queryKey,
  })

  const budgetData = useMemo<BudgetStats>(
    () =>
      data ?? { budgets: [], totalBudget: 0, totalIncome: 0, totalSpend: 0 },
    [data],
  )

  return (
    <ShadowBox
      w={{
        base: '100%',
        lg: '70%',
      }}
    >
      {isFetching || isLoading ? (
        <Loader isMinimized />
      ) : (
        <>
          <Flex className={classes.header}>
            <Flex className={classes.title}>
              <Title mb={0} mr={16} variant="h2">
                Budget
              </Title>

              <Text variant="body_2_black">({today.format('MMMM')})</Text>
            </Flex>

            <Title mb={0} variant="h1">
              {formatMoney(budgetData.totalBudget, 'USD')}
            </Title>
          </Flex>

          {budgetData.budgets.length ? (
            <ScrollArea h={216} scrollbars="x" w="100%">
              <Flex gap={8}>
                {budgetData.budgets.map(budget => (
                  <BudgetsWidgetItem key={budget.id} budget={budget} />
                ))}
              </Flex>
            </ScrollArea>
          ) : (
            <Flex align="center" direction="column" justify="center">
              <Text>No budgets added</Text>
              <Text variant="body_2_black">
                {!isUser
                  ? 'Add a budget to see it here'
                  : 'Ask employee to add budget'}
              </Text>
            </Flex>
          )}
        </>
      )}
    </ShadowBox>
  )
}
